import { css, html, LitElement } from "lit";

import { FLIGHT_SAMPLE_INDEX } from "../../constants";

export class FlightMapComponent extends LitElement {
  static properties = {
    samples: { type: Array },
    playIndex: { type: Number, state: true },
    isPlaying: { type: Boolean, state: true },
    playbackSpeed: { type: Number, state: true },
  };

  static styles = [
    css`
      :host {
        display: block;
        width: 100%;
      }

      #map {
        width: 100%;
        height: 58vh;
        min-height: 380px;
        border-radius: 10px;
        overflow: hidden;
        border: 1px solid #c7d5e3;
      }

      .controls {
        margin-top: 10px;
        border: 1px solid #d9e2ec;
        border-radius: 10px;
        padding: 10px;
        background: linear-gradient(180deg, #f8fbff, #eef3f8);
      }

      .controls-row {
        display: flex;
        align-items: center;
        gap: 10px;
      }

      button {
        border: 1px solid #98afc4;
        background: #fff;
        color: #0f172a;
        border-radius: 8px;
        padding: 6px 10px;
        font-weight: 600;
        cursor: pointer;
      }

      button:hover {
        background: #f1f5f9;
      }

      input[type="range"] {
        flex: 1;
      }

      select {
        border: 1px solid #98afc4;
        background: #fff;
        color: #0f172a;
        border-radius: 8px;
        padding: 6px;
      }

      .meta {
        margin-top: 8px;
        font-size: 12px;
        color: #475569;
      }

      @media (max-width: 768px) {
        #map {
          height: 48vh;
          min-height: 300px;
        }

        .controls-row {
          flex-wrap: wrap;
        }
      }
    `,
  ];

  constructor() {
    super();
    /** @type {number[][]} */
    this.samples = [];
    this.playIndex = 0;
    this.isPlaying = false;
    this.playbackSpeed = 1;
    this.map = null;
    this.marker = null;
    /** @type {number[][]} */
    this.trackPoints = [];
    /** @type {number} */
    this.minAltFt = 0;
    /** @type {number} */
    this.maxAltFt = 0;
    /** @type {number} */
    this.avgAltFt = 0;
    this.lastTrackSignature = "";
    this.playTimer = null;
    this.liveMode = false;
    this.moveMap = true;

    this.onSliderInput = this.onSliderInput.bind(this);
    this.onSpeedChange = this.onSpeedChange.bind(this);
  }

  async firstUpdated() {
    const maplibregl = await this.ensureMapLibreLoaded();

    const cssLink = document.createElement("link");
    cssLink.rel = "stylesheet";
    cssLink.href = "https://unpkg.com/maplibre-gl@5.7.0/dist/maplibre-gl.css";
    cssLink.setAttribute("data-maplibre", "css");
    this.shadowRoot.appendChild(cssLink);

    const mapEl = this.shadowRoot.querySelector("#map");

    this.map = new maplibregl.Map({
      container: mapEl,
      style: "https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json",
      center: [0, 0],
      zoom: 10,
      pitch: 65,
      bearing: 0,
      antialias: true,
      minZoom: 6,
    });

    this.map.addControl(
      new maplibregl.NavigationControl({
        visualizePitch: true,
        position: "top-right",
      })
    );

    this.map.on("load", () => {
      this.refreshTrack();
    });
  }

  /**
   * Load MapLibre JS and CSS from CDN once and return the global object.
   * @returns {Promise<any>}
   */
  async ensureMapLibreLoaded() {
    const maplibreWindow = /** @type {any} */ (window);
    if (maplibreWindow.maplibregl) {
      return maplibreWindow.maplibregl;
    }

    await new Promise((resolve, reject) => {
      const existing = document.querySelector('script[data-maplibre="js"]');
      if (existing) {
        existing.addEventListener("load", resolve, { once: true });
        existing.addEventListener("error", reject, { once: true });
        return;
      }

      const script = document.createElement("script");
      script.src = "https://unpkg.com/maplibre-gl@5.7.0/dist/maplibre-gl.js";
      script.async = true;
      script.setAttribute("data-maplibre", "js");
      script.addEventListener("load", resolve, { once: true });
      script.addEventListener("error", reject, { once: true });
      document.head.appendChild(script);
    });

    return maplibreWindow.maplibregl;
  }

  disconnectedCallback() {
    this.stopPlayback();
    super.disconnectedCallback();
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }

  /**
   * @param {Map<PropertyKey, unknown>} changedProperties
   */
  updated(changedProperties) {
    if (
      changedProperties.has("samples") &&
      this.map &&
      this.map.isStyleLoaded()
    ) {
      this.refreshTrack();
    }
  }

  /**
   * Convert raw samples into valid map points and recalculate derived altitude stats.
   */
  buildTrackData() {
    this.trackPoints = (this.samples || []).filter(
      (sample) =>
        Array.isArray(sample) &&
        Number.isFinite(sample[FLIGHT_SAMPLE_INDEX.LAT]) &&
        Number.isFinite(sample[FLIGHT_SAMPLE_INDEX.LON])
    );

    if (this.trackPoints.length === 0) {
      this.minAltFt = 0;
      this.maxAltFt = 0;
      this.avgAltFt = 0;
      return;
    }

    const altitudes = this.trackPoints
      .map((sample) => Number(sample[FLIGHT_SAMPLE_INDEX.ALT] || 0))
      .filter(Number.isFinite);

    this.minAltFt = altitudes.length ? Math.min(...altitudes) : 0;
    this.maxAltFt = altitudes.length ? Math.max(...altitudes) : 0;
    this.avgAltFt =
      altitudes.length > 0
        ? altitudes.reduce((acc, alt) => acc + alt, 0) / altitudes.length
        : 0;
  }

  /**
   * @param {number} altFt
   * @returns {number}
   */
  normalizeAltitude(altFt) {
    const range = this.maxAltFt - this.minAltFt;
    if (!Number.isFinite(range) || range <= 0) {
      return 0.5;
    }
    return Math.max(0, Math.min(1, (altFt - this.minAltFt) / range));
  }

  /**
   * Build one LineString feature per segment up to upToIndex, each carrying
   * altNorm so the line layer can colour it by altitude.
   * @param {number} upToIndex
   * @returns {any}
   */
  buildPlayedSegments(upToIndex) {
    /** @type {any[]} */
    const features = [];
    const limit = Math.min(upToIndex, this.trackPoints.length - 1);
    for (let i = 1; i <= limit; i++) {
      const a = this.trackPoints[i - 1];
      const b = this.trackPoints[i];
      const altFt =
        (Number(a[FLIGHT_SAMPLE_INDEX.ALT] || 0) +
          Number(b[FLIGHT_SAMPLE_INDEX.ALT] || 0)) /
        2;
      features.push({
        type: "Feature",
        properties: { altFt },
        geometry: {
          type: "LineString",
          coordinates: [
            [a[FLIGHT_SAMPLE_INDEX.LON], a[FLIGHT_SAMPLE_INDEX.LAT]],
            [b[FLIGHT_SAMPLE_INDEX.LON], b[FLIGHT_SAMPLE_INDEX.LAT]],
          ],
        },
      });
    }
    return { type: "FeatureCollection", features };
  }

  /**
   * Build Polygon features per segment for fill-extrusion (3D height).
   * Each segment is subdivided into steps that linearly interpolate altitude
   * so the top of the extrusion slopes from the previous point's altitude to
   * the current point's altitude rather than being flat.
   * @param {number} upToIndex
   * @returns {any}
   */
  buildPlayedPolygons(upToIndex) {
    /** @type {any[]} */
    const features = [];
    const halfWidthMeters = 50;
    const steps = 10;
    const limit = Math.min(upToIndex, this.trackPoints.length - 1);
    for (let i = 1; i <= limit; i++) {
      const a = this.trackPoints[i - 1];
      const b = this.trackPoints[i];
      const aLat = Number(a[FLIGHT_SAMPLE_INDEX.LAT]);
      const aLon = Number(a[FLIGHT_SAMPLE_INDEX.LON]);
      const bLat = Number(b[FLIGHT_SAMPLE_INDEX.LAT]);
      const bLon = Number(b[FLIGHT_SAMPLE_INDEX.LON]);
      const aAltFt = Number(a[FLIGHT_SAMPLE_INDEX.ALT] || 0);
      const bAltFt = Number(b[FLIGHT_SAMPLE_INDEX.ALT] || 0);
      const midLat = (aLat + bLat) / 2;
      const cosLat = Math.max(0.1, Math.cos((midLat * Math.PI) / 180));
      const dxM = (bLon - aLon) * 111320 * cosLat;
      const dyM = (bLat - aLat) * 111320;
      const len = Math.hypot(dxM, dyM);
      if (!Number.isFinite(len) || len < 0.5) continue;
      const pLat = ((-dxM / len) * halfWidthMeters) / 111320;
      const pLon = ((dyM / len) * halfWidthMeters) / (111320 * cosLat);
      for (let s = 0; s < steps; s++) {
        const t0 = s / steps;
        const t1 = (s + 1) / steps;
        const lat0 = aLat + (bLat - aLat) * t0;
        const lon0 = aLon + (bLon - aLon) * t0;
        const lat1 = aLat + (bLat - aLat) * t1;
        const lon1 = aLon + (bLon - aLon) * t1;
        // Height at the end of this sub-step so the top slopes from aAlt to bAlt.
        const altFt = aAltFt + (bAltFt - aAltFt) * t1;
        const altMeters = Math.max(10, altFt * 0.3048);
        features.push({
          type: "Feature",
          properties: { altFt, altMeters },
          geometry: {
            type: "Polygon",
            coordinates: [
              [
                [lon0 - pLon, lat0 - pLat],
                [lon0 + pLon, lat0 + pLat],
                [lon1 + pLon, lat1 + pLat],
                [lon1 - pLon, lat1 - pLat],
                [lon0 - pLon, lat0 - pLat],
              ],
            ],
          },
        });
      }
    }
    return { type: "FeatureCollection", features };
  }

  /**
   * Build a plane-shaped polygon (arrow) centered at lat/lon, rotated by heading,
   * extruded from ground to altMeters so its top sits at flight altitude.
   * @param {number} lat
   * @param {number} lon
   * @param {number} headingDeg
   * @param {number} altMeters
   * @returns {any}
   */
  buildPlanePolygon(lat, lon, headingDeg, altMeters) {
    // Plane silhouette in local metres, pointing north (heading 0). x=east, y=north.
    // Traced to match the top-down aircraft icon (fuselage + swept main wings + rear stabilisers).
    const shape = [
      [0, 300], // nose tip
      [16, 250], // right fuselage near nose
      [20, 60], // right fuselage at wing leading-edge root
      [250, -50], // right main wing tip (leading corner)
      [220, -90], // right main wing tip (trailing corner)
      [100, -80], // right wing trailing-edge kink (mid-span)
      [28, -60], // right wing root trailing edge
      [20, -200], // right fuselage aft of wing
      [85, -230], // right stab leading-edge root
      [100, -265], // right stab tip (leading corner)
      [80, -300], // right stab tip (trailing corner)
      [18, -285], // right stab root trailing
      [0, -270], // centre tail end
      [-18, -285], // left stab root trailing
      [-80, -300], // left stab tip (trailing corner)
      [-100, -265], // left stab tip (leading corner)
      [-85, -230], // left stab leading-edge root
      [-20, -200], // left fuselage aft of wing
      [-28, -60], // left wing root trailing edge
      [-100, -80], // left wing trailing-edge kink (mid-span)
      [-220, -90], // left main wing tip (trailing corner)
      [-250, -50], // left main wing tip (leading corner)
      [-20, 60], // left fuselage at wing leading-edge root
      [-16, 250], // left fuselage near nose
      [0, 300], // close
    ];

    const rad = (headingDeg * Math.PI) / 180;
    const cosH = Math.cos(rad);
    const sinH = Math.sin(rad);
    const cosLat = Math.max(0.1, Math.cos((lat * Math.PI) / 180));

    const coords = shape.map(([x, y]) => {
      // Clockwise rotation by headingDeg
      const rx = x * cosH + y * sinH;
      const ry = -x * sinH + y * cosH;
      return [lon + rx / (111320 * cosLat), lat + ry / 111320];
    });

    const thickness = 40; // metres
    return {
      type: "Feature",
      properties: {
        altMeters: altMeters + thickness,
        altBase: Math.max(0, altMeters),
      },
      geometry: { type: "Polygon", coordinates: [coords] },
    };
  }

  /**
   * Build the unplayed remainder of the track as a single dim line.
   * @param {number} fromIndex
   * @returns {any}
   */
  buildFutureLineFeature(fromIndex) {
    const remaining = this.trackPoints.slice(Math.max(0, fromIndex));
    return {
      type: "Feature",
      properties: {},
      geometry: {
        type: "LineString",
        coordinates: remaining.map((s) => [
          s[FLIGHT_SAMPLE_INDEX.LON],
          s[FLIGHT_SAMPLE_INDEX.LAT],
        ]),
      },
    };
  }

  ensureLayers() {
    if (!this.map) {
      return;
    }

    if (!this.map.getSource("flight-played")) {
      this.map.addSource("flight-played", {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });
    }

    if (!this.map.getSource("flight-played-poly")) {
      this.map.addSource("flight-played-poly", {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });
    }

    if (!this.map.getSource("flight-future")) {
      this.map.addSource("flight-future", {
        type: "geojson",
        data: {
          type: "Feature",
          properties: {},
          geometry: { type: "LineString", coordinates: [] },
        },
      });
    }

    // Dim line for the unplayed portion.
    if (!this.map.getLayer("flight-future-layer")) {
      this.map.addLayer({
        id: "flight-future-layer",
        type: "line",
        source: "flight-future",
        layout: { "line-join": "round", "line-cap": "round" },
        paint: {
          "line-color": "#3a5068",
          "line-width": 2,
          "line-opacity": 0.5,
          "line-dasharray": [2, 3],
        },
      });
    }

    // Core line — same colour bands, brighter.
    if (!this.map.getLayer("flight-played-core")) {
      this.map.addLayer({
        id: "flight-played-core",
        type: "line",
        source: "flight-played",
        layout: { "line-join": "round", "line-cap": "round" },
        paint: {
          "line-color": "#999",
          "line-width": 3,
          "line-opacity": 1,
        },
      });
    }

    // 3D fill-extrusion — altitude-coloured walls, visible when map is pitched.
    if (!this.map.getLayer("flight-played-extrusion")) {
      this.map.addLayer({
        id: "flight-played-extrusion",
        type: "fill-extrusion",
        source: "flight-played-poly",
        paint: {
          "fill-extrusion-color": [
            "step",
            ["get", "altFt"],
            "#ffe033",
            10000,
            "#00dd77",
            20000,
            "#4d90ff",
            30000,
            "#c471ed",
          ],
          "fill-extrusion-height": ["get", "altMeters"],
          "fill-extrusion-base": 0,
          "fill-extrusion-opacity": 0.75,
        },
      });
    }

    // 3-D plane polygon — extruded fill-extrusion matching the track height.
    if (!this.map.getSource("flight-plane")) {
      this.map.addSource("flight-plane", {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });
    }

    if (!this.map.getLayer("flight-plane-layer")) {
      this.map.addLayer({
        id: "flight-plane-layer",
        type: "fill-extrusion",
        source: "flight-plane",
        paint: {
          "fill-extrusion-color": "#ffffff",
          "fill-extrusion-height": ["get", "altMeters"],
          "fill-extrusion-base": ["get", "altBase"],
          "fill-extrusion-opacity": 1.0,
        },
      });
    }
  }

  refreshTrack() {
    if (!this.map) {
      return;
    }

    const maplibregl = /** @type {any} */ (window).maplibregl;
    if (!maplibregl) {
      return;
    }

    this.buildTrackData();
    this.ensureLayers();

    const signature = `${this.trackPoints.length}:${this.trackPoints[0]?.[FLIGHT_SAMPLE_INDEX.TIMESTAMP] || 0}:${this.trackPoints[this.trackPoints.length - 1]?.[FLIGHT_SAMPLE_INDEX.TIMESTAMP] || 0}`;
    if (signature !== this.lastTrackSignature) {
      this.lastTrackSignature = signature;
      this.playIndex = 0;
      if (this.liveMode) {
        this.playIndex = this.trackPoints.length - 1;
      }
      this.stopPlayback();
      this.centerMapOnIndex(this.playIndex);
    }

    this.updatePlaybackVisuals();
    this.fitToTrack();
  }

  fitToTrack() {
    if (!this.map || this.trackPoints.length < 2) {
      return;
    }
    const maplibregl = /** @type {any} */ (window).maplibregl;
    if (!maplibregl) {
      return;
    }

    const coords = this.trackPoints.map((sample) => [
      sample[FLIGHT_SAMPLE_INDEX.LON],
      sample[FLIGHT_SAMPLE_INDEX.LAT],
    ]);
    if (coords.length < 2) {
      return;
    }

    if (this.moveMap === false) {
      return;
    }
    this.moveMap = false;

    this.centerMapOnIndex(this.playIndex, false);
  }

  updatePlaybackVisuals() {
    if (!this.map || this.trackPoints.length === 0) {
      return;
    }

    const maplibregl = /** @type {any} */ (window).maplibregl;
    if (!maplibregl) {
      return;
    }

    this.playIndex = Math.max(
      0,
      Math.min(this.playIndex, this.trackPoints.length - 1)
    );

    const playedSource = this.map.getSource("flight-played");
    if (playedSource) {
      playedSource.setData(this.buildPlayedSegments(this.playIndex));
    }

    const playedPolySource = this.map.getSource("flight-played-poly");
    if (playedPolySource) {
      playedPolySource.setData(this.buildPlayedPolygons(this.playIndex));
    }

    const futureSource = this.map.getSource("flight-future");
    if (futureSource) {
      futureSource.setData(this.buildFutureLineFeature(this.playIndex));
    }

    const point = this.trackPoints[this.playIndex];
    const altFt = Number.isFinite(point[FLIGHT_SAMPLE_INDEX.ALT])
      ? Number(point[FLIGHT_SAMPLE_INDEX.ALT])
      : 0;
    const altMeters = Math.max(10, altFt * 0.3048);
    const heading = Number.isFinite(point[FLIGHT_SAMPLE_INDEX.HEADING])
      ? point[FLIGHT_SAMPLE_INDEX.HEADING]
      : 0;

    const planeSource = this.map.getSource("flight-plane");
    if (planeSource) {
      planeSource.setData(
        this.buildPlanePolygon(
          point[FLIGHT_SAMPLE_INDEX.LAT],
          point[FLIGHT_SAMPLE_INDEX.LON],
          heading,
          altMeters
        )
      );
    }
  }

  togglePlayback() {
    if (!this.trackPoints.length) {
      return;
    }

    if (this.isPlaying) {
      this.stopPlayback();
      return;
    }

    if (this.playIndex >= this.trackPoints.length - 1) {
      this.playIndex = 0;
      this.updatePlaybackVisuals();
    }

    this.isPlaying = true;
    this.playTimer = setInterval(() => {
      const step = 1;
      this.playIndex += step;
      if (this.playIndex >= this.trackPoints.length - 1) {
        this.playIndex = this.trackPoints.length - 1;
        this.stopPlayback();
      }
      this.updatePlaybackVisuals();
      this.centerMapOnIndex(this.playIndex);
      this.requestUpdate();
    }, 1000 / this.playbackSpeed);
  }

  stopPlayback() {
    this.isPlaying = false;
    if (this.playTimer) {
      clearInterval(this.playTimer);
      this.playTimer = null;
    }
  }

  /**
   * @param {Event} event
   */
  onSliderInput(event) {
    const target = /** @type {HTMLInputElement} */ (event.target);
    this.playIndex = Number(target.value || 0);
    this.centerMapOnIndex(this.playIndex, false);
    this.updatePlaybackVisuals();
  }

  /**
   * Center the map on the coordinate at the given sample index, if valid.
   * @param {number} index
   * @param {boolean} [ease=true]
   * @returns {void}
   */
  centerMapOnIndex(index, ease = true) {
    if (!this.map || this.trackPoints.length === 0) {
      return;
    }
    const coord = this.trackPoints[index];
    if (!coord) {
      return;
    }
    this.map.easeTo({
      center: [coord[FLIGHT_SAMPLE_INDEX.LON], coord[FLIGHT_SAMPLE_INDEX.LAT]],
      duration: ease ? 800 : 0,
    });
  }

  /**
   * @param {Event} event
   */
  onSpeedChange(event) {
    const target = /** @type {HTMLSelectElement} */ (event.target);
    this.playbackSpeed = Number(target.value || 1);
    this.stopPlayback();
    this.togglePlayback();
  }

  render() {
    const current = this.trackPoints[this.playIndex] || [];
    const sampleTime = current[FLIGHT_SAMPLE_INDEX.TIMESTAMP]
      ? new Date(current[FLIGHT_SAMPLE_INDEX.TIMESTAMP]).toLocaleTimeString()
      : "-";
    const sampleAlt = Number(current[FLIGHT_SAMPLE_INDEX.ALT] || 0);
    const sampleSpeed = Number(current[FLIGHT_SAMPLE_INDEX.SPEED] || 0);

    // generate vertical speed (climb/descent rate) in feet per minute, using altitude change over last 20 seconds of samples
    let sampleVS = 0;
    for (let i = this.playIndex - 1; i >= 0; i--) {
      const prev = this.trackPoints[i];
      if (!prev) continue;
      const timeDiff =
        current[FLIGHT_SAMPLE_INDEX.TIMESTAMP] -
        prev[FLIGHT_SAMPLE_INDEX.TIMESTAMP];
      if (timeDiff > 20000) break; // limit to last 20 seconds
      const altDiff = sampleAlt - Number(prev[FLIGHT_SAMPLE_INDEX.ALT] || 0);
      sampleVS = (altDiff / timeDiff) * 60000; // feet per minute
      break;
    }

    return html`
      <div id="map"></div>
      <div class="controls">
        <div class="controls-row">
          <button @click=${() => this.togglePlayback()}>
            ${this.isPlaying ? "Pause" : "Play"}
          </button>
          <input
            type="range"
            min="0"
            max="${Math.max(0, this.trackPoints.length - 1)}"
            .value=${String(this.playIndex)}
            @input=${this.onSliderInput}
          />
          <select
            .value=${String(this.playbackSpeed)}
            @change=${this.onSpeedChange}
            title="Playback speed"
          >
            <option value="1">1x</option>
            <option value="2">2x</option>
            <option value="4">4x</option>
            <option value="8">8x</option>
          </select>
        </div>
        <div class="meta">
          ${sampleTime}
          • ${Math.round(sampleAlt).toLocaleString()} ft
          • ${Math.round(sampleSpeed).toLocaleString()} kt
          • ${Math.round(sampleVS).toLocaleString()} ft/min
        </div>
      </div>
    `;
  }
}

customElements.define("flight-map-component", FlightMapComponent);
